import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import './SpotsTab.css';


function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

function daysLeft(lockedUntil) {
  const diff = new Date(lockedUntil) - Date.now();
  return Math.max(0, Math.ceil(diff / (24 * 60 * 60 * 1000)));
}

function detectLinkType(url) {
  if (!url) return 'other';
  const u = url.toLowerCase();
  if (u.includes('spotify.com/artist') || (u.includes('music.apple.com') && u.includes('/artist'))) return 'artist';
  if (u.includes('spotify.com/album') || (u.includes('music.apple.com') && u.includes('/album'))) return 'album';
  if (u.includes('spotify.com/track')) return 'track';
  return 'other';
}

const TYPE_LABELS = { artist: '🎤 Artist', album: '💿 Album', track: '🎵 Track', other: '🔗 Link' };
const TYPE_COLORS = { artist: '#a78bfa', album: '#34d399', track: '#60a5fa', other: '#9ca3af' };

export default function SpotsTab() {
  const [totalSpots, setTotalSpots] = useState(0);
  const [spots, setSpots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(null); // spotIndex being submitted
  const [inputs, setInputs] = useState({});           // { [spotIndex]: string }
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState({});

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/spots/my');
      setTotalSpots(res.data.totalSpots || 0);
      setSpots(res.data.spots || []);
    } catch {
      // no spots yet
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const spotMap = {};
  spots.forEach(s => { spotMap[s.spotIndex] = s; });

  const handleSubmit = async (spotIndex) => {
    const link = (inputs[spotIndex] || '').trim();
    if (!link) {
      setErrors(e => ({ ...e, [spotIndex]: 'Please enter a link' }));
      return;
    }
    setErrors(e => ({ ...e, [spotIndex]: '' }));
    setSubmitting(spotIndex);
    try {
      await api.post('/spots/submit', { spotIndex, link });
      setSuccess(s => ({ ...s, [spotIndex]: 'Spot saved! Locked for 3 months.' }));
      setInputs(i => ({ ...i, [spotIndex]: '' }));
      load();
    } catch (err) {
      setErrors(e => ({ ...e, [spotIndex]: err?.response?.data?.message || 'Failed to submit' }));
    } finally {
      setSubmitting(null);
    }
  };

  if (loading) return <div className="spots-loading">Loading your spots…</div>;

  if (totalSpots === 0) {
    return (
      <div className="spots-empty">
        <div className="spots-empty-icon">🎯</div>
        <h3>No Spots Allocated</h3>
        <p>You don't have any spots yet. Spots are assigned by the admin — contact support to get yours.</p>
      </div>
    );
  }

  return (
    <div className="spots-root">
      <div className="spots-header">
        <h2 className="spots-title">Your Spots</h2>
        <p className="spots-subtitle">
          Add an artist, album, or track link to each spot. Once submitted, a spot is locked for <strong>3 months</strong>.
        </p>
      </div>

      <div className="spots-grid">
        {Array.from({ length: totalSpots }, (_, i) => {
          const spot = spotMap[i];
          const isLocked = spot && new Date(spot.lockedUntil) > new Date();
          const days = spot ? daysLeft(spot.lockedUntil) : 0;
          const detectedType = inputs[i] ? detectLinkType(inputs[i]) : null;

          return (
            <div key={i} className={`spot-card ${isLocked ? 'spot-card-locked' : spot ? 'spot-card-unlocked' : 'spot-card-empty'}`}>
              <div className="spot-card-header">
                <span className="spot-number">Spot {i + 1}</span>
                {isLocked && <span className="spot-lock-badge">🔒 {days}d left</span>}
                {spot && !isLocked && <span className="spot-unlocked-badge">🔓 Editable</span>}
                {!spot && <span className="spot-empty-badge">Empty</span>}
              </div>

              {isLocked ? (
                <div className="spot-filled">
                  <div className="spot-type-badge" style={{ color: TYPE_COLORS[spot.linkType] }}>
                    {TYPE_LABELS[spot.linkType]}
                  </div>
                  <a href={spot.link} target="_blank" rel="noopener noreferrer" className="spot-link">
                    {spot.link}
                  </a>
                  <div className="spot-meta">
                    Submitted {formatDate(spot.submittedAt)} · Editable after {formatDate(spot.lockedUntil)}
                  </div>
                </div>
              ) : (
                <div className="spot-form">
                  {spot && !isLocked && (
                    <div className="spot-prev-link">
                      <span className="spot-prev-label">Current:</span>
                      <a href={spot.link} target="_blank" rel="noopener noreferrer">{spot.link}</a>
                    </div>
                  )}
                  <div className="spot-input-row">
                    {detectedType && (
                      <span className="spot-detected" style={{ color: TYPE_COLORS[detectedType] }}>
                        {TYPE_LABELS[detectedType]}
                      </span>
                    )}
                    <input
                      className="spot-input"
                      type="url"
                      placeholder="Paste Spotify / Apple Music link…"
                      value={inputs[i] || ''}
                      onChange={e => {
                        setInputs(v => ({ ...v, [i]: e.target.value }));
                        setErrors(v => ({ ...v, [i]: '' }));
                        setSuccess(v => ({ ...v, [i]: '' }));
                      }}
                      onKeyDown={e => e.key === 'Enter' && handleSubmit(i)}
                    />
                    <button
                      className="spot-submit-btn"
                      onClick={() => handleSubmit(i)}
                      disabled={submitting === i}
                    >
                      {submitting === i ? '…' : 'Save'}
                    </button>
                  </div>
                  {errors[i] && <div className="spot-error">{errors[i]}</div>}
                  {success[i] && <div className="spot-success">{success[i]}</div>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
